/* components/EastSlider.ts
Uses https://swiperjs.com/demos
Settings are supported, use the inline navigation, pagination and scrollbar for best results
CSS -> for each slider, recommend adding css for class 'swiper-slide' of:
    (100% / number of slides shown) - (((number of slides - 1) * gap) / number of slides)
    eg, 4 slides and 20px gap: width: calc(25% - 15px)
    eg, 6 slides and 15px gap: width: calc((100% / 6) - ((5 * 15px) / 6))
Don't have to add the 'swiper-slide' class to the slides but would be beneficial
Use within liquid as <east-slider>

Example
<east-slider
    navigation="true",
    pagination="true"
    settings='{"slidesPerView": 3, "loop": true, "pagination":{"type":"bullets"}}'
>
    <div class="swiper-slide">
        Slide Content Here
    </div>
    <div class="swiper-slide">
        Slide Content Here
    </div>...
</east-slider>
*/

import { Component, Prop } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import Swiper, { Navigation, Pagination, Scrollbar, SwiperOptions } from 'swiper';
import 'swiper/css/bundle';

@Component
export class EastSlider extends ThemeComponent {
    @Prop({ default: false}) navigation!: boolean;
    @Prop({ default: ''}) svg!: string;
    @Prop({ default: false}) pagination!: boolean;
    @Prop({ default: false}) scrollbar!: boolean;
    @Prop() settings!: SwiperOptions;

    init() {
        if (!this.svg && this.navigation) {
            this.svg = `<svg role="presentation" viewBox="0 0 21 11">
            <polyline 
                fill="none" 
                stroke="currentColor" 
                points="20.5 10.5 10.5 0.5 0.5 10.5" 
                stroke-width="1.25">
            </polyline>
            </svg>`;
        }
        this.setSlider();
        this.addSlides();
        if (this.navigation || this.pagination || this.scrollbar) {
            this.checkProps();
        }
        this.initateSwiper();
    }
    setSlider() {
        // add classes to make slider work
        this.classList.add('swiper');
        const children = this.children;
        for (const child of children) {
            if(!child.classList.contains('swiper-slide')) {
                child.classList.add('swiper-slide');
            }
        }
    }
    addSlides() {
        const content = this.innerHTML;
        this.innerHTML = `<div class="swiper-wrapper">${content}</div>`;
    }
    checkProps() {
        if (this.navigation) {
            // add navigation to the elem
            const buttons = `<button 
                    role="button" 
                    type="button" 
                    class="swiper-next swiper-navigation flex f-vertical-center f-center"
                >
                    ${this.svg}
                </button>
                <button 
                    role="button" 
                    type="button" 
                    class="swiper-prev swiper-navigation flex f-vertical-center f-center"
                >
                    ${this.svg}
                </button>`;
            this.innerHTML = this.innerHTML + buttons;
            this.settings.navigation = {nextEl: ".swiper-next", prevEl: ".swiper-prev"};
        }
        if (this.pagination || this.settings.pagination) {
            // add pagination to the elem
            this.innerHTML = this.innerHTML + '<div class="swiper-pagination"></div>';

            // assign type of pagination
            const pagination = (typeof this.settings.pagination == 'object')
                ? this.settings.pagination.type
                : 'bullets';
            this.settings.pagination = {el: '.swiper-pagination', type: pagination, clickable: true};
            this.$log.debug('settings', this.settings);
        }
        if (this.scrollbar || this.settings.scrollbar) {
            // add scrollbar to the elem
            this.innerHTML = this.innerHTML + '<div class="swiper-scrollbar"></div>';
            this.settings.scrollbar = {el: '.swiper-scrollbar', hide: false};
        }
    }
    initateSwiper() {
        // init slider
        Swiper.use([Navigation, Pagination, Scrollbar]);
        const slider = new Swiper(this, this.settings);
        const images = this.querySelectorAll('img[data-preload]');
        for (const image of images) {
            if (image.getAttribute('data-preload') !== 'loaded') {
                const src = image.getAttribute('data-preload') ?
                    image.getAttribute('data-preload') :
                    image.getAttribute('src');
                if (src) {
                    image.setAttribute('src', src);
                    image.setAttribute('data-preload', 'loaded');
                }
            }
        }
        this.$log.info('Slider Active', slider);
    }
}
